import React, { useEffect, useState, useRef } from 'react';

// Import your images directly
import img1 from '../assets/1.png';
import img2 from '../assets/2.png';
import img3 from '../assets/3.png';
import img4 from '../assets/4.png';
import img5 from '../assets/5.png';
import img6 from '../assets/6.png';
import chefImg from '../assets/chef.png';

const features = [
  { label: 'Cosy Ambience', image: img1 },
  { label: 'Fresh Ingredients', image: img2 },
  { label: 'Fast Service', image: img3 },
  { label: 'Best Qualities', image: img4 },
  { label: 'Outdoor Seating', image: img5 },
  { label: 'Delicious Cuisine', image: img6 },
];

const EleganceRedefined = ({ id }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 650);
  const [inView, setInView] = useState(false); // State for section visibility
  const sectionRef = useRef(null); // Ref for observing the section

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 650);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Intersection Observer for scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Trigger animation when section is at least 10% visible
          setInView(entry.isIntersecting);
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the component is visible
      }
    );

    const currentSection = sectionRef.current; // Capture current ref value

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []); // Empty dependency array means this runs once on mount

  // Helper for scroll-triggered text animations (slide up, fade in)
  const getScrollAnimatedStyle = (delay) => ({
    transform: inView ? 'translateY(0)' : 'translateY(50px)',
    opacity: inView ? 1 : 0,
    transition: `transform 0.8s ease-out ${delay}s, opacity 0.8s ease-out ${delay}s`,
  });

  const sectionStyle = {
    backgroundColor: '#fff',
    padding: '60px 20px',
    overflow: 'hidden', // Crucial to hide elements before they slide in
  };

  const contentStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '40px',
    flexDirection: isMobile ? 'column' : 'row',
  };

  const chefImgContainerStyle = {
    flex: '1',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '100%',
    // Apply scroll animation to the image container
    ...getScrollAnimatedStyle(0.6), // Slightly delayed after text
  };

  const imgStyle = {
    maxWidth: '80%',
    height: 'auto',
    borderRadius: '10px',
  };

  const textContainerStyle = {
    flex: '1',
    minWidth: '300px',
    maxWidth: '800px',
    textAlign: isMobile ? 'center' : 'left',
  };

  const subheadingStyle = {
    color: '#d4a762',
    fontWeight: '500',
    fontSize: '18px',
    marginBottom: '10px',
    letterSpacing: '1px',
    fontFamily: 'Playfair Display, serif',
  };

  const headingStyle = {
    fontSize: isMobile ? '30px' : '38px', // Responsive font size
    marginBottom: '15px',
    color: '#222',
    fontFamily: 'Playfair Display, serif',
    fontWeight: '500', // Set to semi-bold
  };
  const headingStyle1 = {
    fontSize: isMobile ? '30px' : '38px', // Responsive font size
    marginBottom: '15px',
    
    fontFamily: 'Playfair Display, serif',
    fontWeight: '500', // Set to semi-bold
  };

  const paragraphStyle = {
    color: '#333',
    marginBottom: '20px',
    lineHeight: '1.6',
    maxWidth: isMobile ? '100%' : '90%', // Full width on small screens
    textAlign: isMobile ? 'center' : 'left', // Center text on small screens
  };

  const featuresGridStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
    gap: '10px 20px',
    marginBottom: '20px',
  };

  const featureStyle = {
    padding: '14px',
    borderRadius: '10px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
  };
  const subtitleStyle = {
    color: '#d4a017',
    fontSize: '18px',
    fontWeight: '500',
    letterSpacing: '1px',
    marginBottom: '25px',
    textTransform: 'uppercase',
    
  };

  const featureImageStyle = {
    width: '60px',
    borderRadius: '50%',
    marginBottom: '8px',
  };

  const featureLabelStyle = {
    color: '#444', // Default label color
  };

  // Base style for buttons (will use CSS classes for specific hover effects)
  const baseButtonStyle = {
    padding: '12px 15px',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    textDecoration: 'none',
    position: 'relative',
    overflow: 'hidden',
    zIndex: 1,
    border: 'none',
    backgroundColor: '#D4A762',
    color: '#fff',
    margin: isMobile ? '0 auto' : 'initial',
  };

  const handleBookTableClick = () => {
    const contactSection = document.getElementById('contact-us');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600&display=swap');

        /* Feature Box Hover Effect (No Shadow) */
        .feature {
          transition: transform 0.3s ease; /* Only transform */
          position: relative;
          overflow: hidden;
          will-change: transform, opacity;
        }

        .feature:hover {
          transform: translateY(-5px); /* Lift effect */
        }

        /* Icon (Image) Hover Effect - Removed color change */
        .feature img {
          transition: transform 0.3s ease; /* Smooth transition for icon */
          /* Filter for color change removed as requested */
        }

        .feature:hover img {
          transform: scale(1.15) rotate(5deg); /* Larger scale and slight rotate on icon hover */
        }

        /* Label (Text) Hover Effect */
        .feature > div { /* Target the label div directly */
          transition: color 0.3s ease; /* Smooth transition for label color */
        }

        .feature:hover > div {
          color: #d4a762; /* Change label color to gold on hover */
        }

        /* Book A Table Button Hover (matching OffersSection) */
        .book-table-button {
          color: #fff; /* Default text color */
          background-color: #D4A762; /* Default background */
          transition: background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;
        }

        .book-table-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 0%;
          height: 100%;
          background-color: #b88e50; /* Darker gold for fill */
          transition: width 0.3s ease-out;
          z-index: -1; /* Behind the text */
        }

        .book-table-button:hover::before {
          width: 100%; /* Expand to full width on hover */
        }

        .book-table-button:hover {
          color: #fff; /* Ensure text is white on hover */
          box-shadow: 0 4px 10px rgba(0,0,0,0.2); /* Subtle shadow on hover for buttons */
        }

        /* Styling for the colored and bolded part of the heading */
        .heading-highlight {
          color: #d4a762; /* Same as button color */
          font-family: 'Playfair Display', serif; /* Ensure consistent font */
          font-weight: bold; /* Set to bold */
        }

        /* Media queries for responsiveness (as per your original code) */
        @media (max-width: 768px) {
          .features-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 480px) {
          .features-grid {
            grid-template-columns: 1fr; /* One column on very small screens */
          }
          .feature {
            width: 100%; /* Ensure full width on very small screens */
          }
        }
      `}} />

      <section id={id} style={sectionStyle} ref={sectionRef}>
        <div style={contentStyle}>
          {/* Image on top if mobile */}
          <div style={chefImgContainerStyle} className="chef-image-container">
            <img src={chefImg} alt="Chef" style={imgStyle} />
          </div>

          {/* Text */}
          <div style={textContainerStyle}>
            <p style={{ ...subtitleStyle, ...getScrollAnimatedStyle(0.1) }}>ELEGANCE REDEFINED</p>
            <h2 style={{ ...headingStyle, ...getScrollAnimatedStyle(0.3) }}>
              Crafts The Perfect <br /> Ambiance For A <span style={{ ...headingStyle1, ...getScrollAnimatedStyle(0.3) }} className="heading-highlight">Truly <br /> Unforgettable Meal.</span>
            </h2>
            <p style={{ ...paragraphStyle, ...getScrollAnimatedStyle(0.5) }}>
              At The Porch Cafe and Restaurant, we create the perfect setting for unforgettable dining.
              With warm lighting and cozy interiors, every meal becomes a special experience.
            </p>

            <div style={featuresGridStyle}>
              {features.map((feature, index) => (
                <div
                  className="feature"
                  style={{
                    ...featureStyle,
                    ...getScrollAnimatedStyle(0.7 + index * 0.1), // Staggered animation
                  }}
                  key={index}
                >
                  <img
                    src={feature.image}
                    alt={feature.label}
                    style={featureImageStyle}
                  />
                  <div style={featureLabelStyle}>{feature.label}</div>
                </div>
              ))}
            </div>

            <button
              style={{ ...baseButtonStyle, ...getScrollAnimatedStyle(0.9) }}
              onClick={handleBookTableClick}
              className="book-table-button"
            >
              Book A Table →
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default EleganceRedefined;